import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { initSentry } from '@/lib/sentry/init';
import './index.css';
import { QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import i18n from '@/lib/i18n';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { RoleProvider } from '@/lib/auth/role-context';
import { Toaster } from '@repo/ui/components/molecules/toaster';
import { queryClient } from '@/api/common/client';
import { NotFoundPage } from './pages/not-found';
import { AppVersionWatcher } from '@/lib/version/app-version-watcher';
import { ErrorFallback } from '@repo/ui/components/molecules/error-fallback';
import { sanitizeBootUrl } from '@/lib/url/sanitize-boot-url';
import { BackofficeRouteError } from '@/pages/session-check-error/route-error';
import { resolveRuntimeBasePath } from '@/lib/runtime-config';
import { BackofficeTableConfigProvider } from '@/providers/table-config-provider';
import { FormConfigProvider } from '@repo/ui/lib/form-config';
import { ThemeProvider } from '@repo/ui/lib/theme-provider';

sanitizeBootUrl();
initSentry();

const router = createRouter({
  routeTree,
  basepath: resolveRuntimeBasePath() || '/',
  defaultPreload: 'intent',
  defaultStaleTime: 5000,
  scrollRestoration: true,
  trailingSlash: 'never',
  defaultNotFoundComponent: NotFoundPage,
  defaultErrorComponent: BackofficeRouteError,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <ThemeProvider>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <RoleProvider>
            <StrictMode>
              <AppVersionWatcher />
              <BackofficeTableConfigProvider>
                {/* Il backoffice non ha permessi per tenant: i form scrivono
                  sempre, i limiti per campo restano locali. */}
                <FormConfigProvider canWrite>
                  <RouterProvider router={router} />
                </FormConfigProvider>
              </BackofficeTableConfigProvider>
              <Toaster />
            </StrictMode>
          </RoleProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </ThemeProvider>
  </Sentry.ErrorBoundary>
);
